import Analytics from "/js/util/analytics.js";
import Util from "/js/util/util.js";
import AppUtil from "/js/util/app-util.js";
import FadeUtil from "/js/util/fade-util.js";
import Global from "/js/core/global.js";
import UserLessons from "/js/core/user-lessons.js";
import InteractionHolderView from "/js/top-level-views/interaction-holder-view.js";
import CardsHolderView from "/js/top-level-views/cards-holder-view.js";
import GalleryView from "/js/top-level-views/gallery-view.js";
import ServiceUtil from "/js/util/service-util";
import LessonJsonUtil from "/js/util/lesson-json-util.js";
import CssUtil from '../util/css-util';

/**
 * Lesson view
 *
 * Owns and coordinates interactions between CardsHolderView, InteractionHolderView, and GalleryView.
 */
export default class LessonView {

  #$el = $("#lesson");

  #cardsHolderView = null;
  #interactionHolderView = null;
  #galleryView = null;

  /**
   * Global values assumed to be already set.
   */
  constructor() {

    this.#interactionHolderView = new InteractionHolderView();

    if (!Global.cmsDevJson) {
      this.#cardsHolderView = new CardsHolderView();
      this.#galleryView = new GalleryView();
    }

    this.#$el.on('interaction-card-animated-out',
        (...rest) => this.#onInteractionCardAnimatedOut(...rest));
    this.#$el.on('interaction-done', (...rest) => this.#onInteractionDone(...rest));
    this.#$el.on('interaction-skip', e => this.#onInteractionSkip(e));
    this.#$el.on('interaction-back', e => this.#onInteractionBack(e));  // Currently not using this.
    this.#$el.on("lesson-lesson-done", e => this.#doLessonDone());

    if (Global.isDev && false) {
      this.#addTestButton();
    }

    this.resize();
  };

  get $el() {
    return this.#$el;
  }

  show(now) {
    Analytics.dashboardButtonTrackingSource = "lesson";

    now
        ? FadeUtil.fadeInNow(this.#$el)
        : FadeUtil.fadeIn(this.#$el);
    this.#cardsHolderView.isEnabled = true;
  }

  hide(now) {
    now
        ? FadeUtil.fadeOutNow(this.#$el)
        : FadeUtil.fadeOut(this.#$el);
    this.#cardsHolderView.isEnabled = false;
  }

  /**
   * @param alwaysFromBeginning forces lesson to start from beginning regardless of previous progress.
   *    Also, if lesson is already complete, will start from beginning.
   */
  startLesson(alwaysFromBeginning=false) {

    if (!Global.lessonData) {
      clw("ERROR: No lesson data");
      return;
    }

    const isRepeatedView = UserLessons.getByLessonId(Global.lessonId) ? "true" : "false";

    if (UserLessons.isComplete(Global.lessonId)) {
      cl('lesson is complete, will start from beginning');
      alwaysFromBeginning = true;
    }
    // cl("LessonView.startLesson() - alwaysFromBeginning:", alwaysFromBeginning, Global.lessonId);

    if (Global.isLiveMode) {
      if (!UserLessons.getByLessonId(Global.lessonId)) {
        ServiceUtil.updateUserLesson(UserLessons.current);
      }
    }

    this.#interactionHolderView.$el.hide();
    this.#cardsHolderView.start(alwaysFromBeginning);

    this.resize();
    this.show();

    Util.windowUnselect();

    Analytics.trackLessonView(isRepeatedView, UserLessons.totalViewedLessonsCount);
  }

  /**
   * Re-shows lesson in its current state (from the minicourse view) .
   */
  reshow() {

    let subtype = null;
    let shouldEnableCardsHolderView = true;
    if (this.isInteraction) {
      subtype = "interaction";
      shouldEnableCardsHolderView = false;
    } else if (this.isGallery) {
      subtype = "gallery";
      shouldEnableCardsHolderView = false;
    }

    AppUtil.updateBodyClasses("lesson", subtype);
    this.#cardsHolderView.isEnabled = shouldEnableCardsHolderView;

    FadeUtil.fadeIn(this.#$el);
  }

  /**
   * Resizes and positions the lesson view and its subviews.
   *
   * <p>Each high-level view generally uses a common scale factor to size and position itself.
   */
  resize() {
    if (this.#interactionHolderView) {
      this.#interactionHolderView.resize();
    }
    if (this.#cardsHolderView) {
      this.#cardsHolderView.resize();
    }
    if (this.#galleryView) {
      this.#galleryView.resize();
    }
  }

  showInteraction(interactionData) {
    if (Global.isLiveMode) {
      ServiceUtil.updateUserLesson(UserLessons.current);
    }
    if (this.#cardsHolderView) {
      this.#cardsHolderView.hide();
    }

    const initialAnswers = (interactionData.shouldSave && !Global.cmsDevJson)
        ? UserLessons.current.answers
        : null;
    this.#interactionHolderView.launch(interactionData, initialAnswers);
    AppUtil.updateBodyClasses("lesson", "interaction");

    if (Global.lessonData) {
      Analytics.trackInteractionStart(Global.lessonData.interactions, interactionData.interactionId);
    }
  }

  showGalleryView(thumb) {
    this.#galleryView.show(thumb);
    this.#cardsHolderView.isEnabled = false;
  }

  closeGalleryView() {
    this.#galleryView.animateOut();
    this.#cardsHolderView.isEnabled = true;
  }

  // TODO: Need enum-like state! ("normal" | "interaction" | "gallery") rather than relying on either dom properties ffs
  /**
   * @return {boolean}
   */
  get isInteraction() {
    const b = CssUtil.isDisplayBlock(this.#interactionHolderView.$el);
    return b;
  }

  /**
   * @return {boolean}
   */
  get isGallery() {
    return $("body").hasClass("isGallery");  // TODO: Again, bad system
  }

  set isEnabled(b) {
    if (b) {
      if (this.isInteraction) {
        this.#interactionHolderView.isEnabled = true;
        this.#cardsHolderView.isEnabled = false;
      } else if (this.isGallery) {
        this.#cardsHolderView.isEnabled = false;
      } else {
        this.#cardsHolderView.isEnabled = true;
      }
    } else {
      this.#cardsHolderView.isEnabled = false;
      if (this.isInteraction) {
        this.#interactionHolderView.isEnabled = false;
      }
    }
  }

  #advanceAfterInteraction() {
    AppUtil.updateBodyClasses("lesson", null);
    const f = () => {
      if (this.#cardsHolderView.isPastLastStack) {
        this.#doLessonDone();
      } else {
        this.#cardsHolderView.doNextStackAfterInteraction();
      }
    };
    this.#interactionHolderView.hide(f);
  }

  #doLessonDone() {
    Analytics.isRepeatedLessonComplete = UserLessons.isComplete(Global.lessonId);

    if (Global.minicourseData) {
      UserLessons.current.setToComplete();
    }
    if (Global.isLiveMode) {
      ServiceUtil.updateUserLesson(UserLessons.current);
    }

    // Send 'ordinal' lesson complete event
    const lessonsComplete = UserLessons.totalCompletedLessonsCount;
    switch (lessonsComplete) {
      case 1:
        Analytics.trackLessonsCompleteOne();
        break;
      case 2:
        Analytics.trackLessonsCompleteTwo();
        break;
      case 3:
        Analytics.trackLessonsCompleteThree();
        break;
      case 4:
        Analytics.trackLessonsCompleteFour();
        break;
      default:
        break;
    }
    Analytics.trackLessonComplete(lessonsComplete, AppUtil.getNextUnfinishedLesson(Global.lessonId));

    // Send set complete event if necessary
    if (Global.minicourseData && Global.minicourseData.lessonIds) {
      let completedLessonsCount = 0;
      for (const lessonId of Global.minicourseData.lessonIds) {
        const userLesson = UserLessons.getByLessonId(lessonId);
        if (userLesson && userLesson.isComplete) {
          completedLessonsCount++;
        }
      }
      if (completedLessonsCount === Global.minicourseData.lessonIds.length
          && !Analytics.isRepeatedLessonComplete) {
        Analytics.trackSetComplete();
      }
    }

    this.#cardsHolderView.$el.hide();
    $(window).trigger("lesson-done");
  }

  #addTestButton() {
    setTimeout(() => {
      const s = `<div id="test1" style="width:140px;height:40px;position:absolute;top:200px;right:0;background:#aaa"></div>`;
      this.#$el.append(s);
      const $el = this.#$el.find("#test1");
      $el.click(()=>this.#cardsHolderView.showLastCard())
    }, 1000);
  }

  // Handlers

  #onInteractionCardAnimatedOut(e, interactionId) {
    // Get the interaction's data  // TODO: put logic in PrimerMaster
    let interactionData = LessonJsonUtil.getInteractionById(interactionId, Global.lessonData) ;
    if (!interactionData) {
      cl("No match for", interactionId);
      return;
    }
    this.showInteraction(interactionData);
  }

  #onInteractionSkip(e) {
    this.#advanceAfterInteraction();
  }

  #onInteractionDone(e, interactionId) {
    Analytics.trackInteractionEnd(Global.lessonData.interactions, interactionId);
    this.#advanceAfterInteraction();
  }

  #onInteractionBack(e) {
    this.#interactionHolderView.hide(() => {
      AppUtil.updateBodyClasses("lesson");
      this.#cardsHolderView.doPreviousStackAfterInteractionBack();
    });
  }
}
